import React, { useEffect, useState, useRef } from 'react';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Collapse from '@mui/material/Collapse';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import NotesIcon from '@mui/icons-material/Notes';
import MenuItemCreatorEditorButton from './MenuItemCreatorEditorButton';
import PropTypes from 'prop-types';
import { removeMenuItem } from '../../../services/kitchenService';
import MenuItemRemoveIconButton from './MenuItemRemoveIconButton';
import { aws_base_path } from '../../../config/config';
import { uploadFile } from '../../../services/uploadService';
import { saveMenuItemData } from '../../../services/kitchenService';
import menuItemPlaceholderGray from '../../../imgs/menu-item-placeholder-gray.svg';

import CameraAltIcon from '@mui/icons-material/CameraAlt';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useAuth } from '../../../AuthContext';
import { useLocale } from '../../../i18n/LanguageContext';

export default function MenuItemReadView(props) {
  const { auth } = useAuth();
  const { t } = useLocale();
  const [error, setError] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const fileMenuItemInputRef = useRef(null);
  const [uploadStatus, setUploadStatus] = useState({inProgress: false, errorMessage: ''});
  const hasItemPicture = Boolean(props.item?.itemPic && String(props.item.itemPic).trim() !== '');
  const menuItemImageSrc = hasItemPicture ? aws_base_path + props.item.itemPic : menuItemPlaceholderGray;

  const handleImageButtonClick = () => {
    fileMenuItemInputRef.current.click();
  };

  const handleImageChange = (event) => {
    setUploadStatus({inProgress: true, errorMessage: ''});
    if (!event.target.files[0]) {
      alert(t('menuItems.selectFile'));
      return;
    }

    handleUpload(event.target.files[0]);
  };

  const handleUpload = (selectedFile) => {
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('metadata', JSON.stringify(
      { 
        chefId: props.chef?.id, 
        kitchenId: props.menu.kitchenId, 
        menuItemId: props.item.id, 
        pictureType: 'menuItem'
      }
    ));

    uploadFile(formData, auth).then((response) => {
      const errMessage = t('menuItems.pictureUploadError');
      if (response) {
        var existingMenu = props.menu; 
        const pos = existingMenu.items.map(elem => elem.id).indexOf(props.item.id);
        if (pos > -1) {
          let menuItemEdited = props.item;
          menuItemEdited.itemPic = response.imageThumbUrl;

          saveMenuItemData(menuItemEdited, auth).then((savedMenuItem) => {
            if (savedMenuItem) {
              existingMenu.items[pos] = savedMenuItem;
              props.onMenuChange(existingMenu);
              setUploadStatus({inProgress: false, errorMessage: ''});
            } else {
              setUploadStatus({inProgress: false, errorMessage: errMessage});
            }
          }).catch(() => {
            setUploadStatus({inProgress: false, errorMessage: errMessage});
          });
        }
      } else {
        setUploadStatus({inProgress: false, errorMessage: errMessage});
      }
    })
      .catch((error) => {
        console.error('Error uploading file:', error);
        setUploadStatus({inProgress: false, errorMessage: t('menuItems.uploadError')});
      });
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const ExpandMore = styled((props) => {
    const { expand, ...other } = props;
    return <IconButton {...other} />;
  })(({ theme, expand }) => ({
    transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
    marginLeft: 'auto',
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  }));

  function handleRemoveMenuItem() {
    removeMenuItem(props.item.id, auth).then((isRemoved) => {
      if (isRemoved) {
        var existingMenu = props.menu; 
        const pos = existingMenu.items.map(elem => elem.id).indexOf(props.item.id);
        if (pos > -1) {
          existingMenu.items.splice(pos, 1); // remove element in memory
          props.onMenuChange(existingMenu);
        }
      }
    })
    // .catch(() => {
    //   setError(true);
    // });
  }

  function handleEditMenuItem(menu) {
    props.onMenuChange(menu);
  }

  return (
    <div>
    { uploadStatus.errorMessage ? (<Alert severity="error">{t('menuItems.uploadFailure', { message: uploadStatus.errorMessage })}</Alert>) : ''}
    <Card sx={{ width: 324 }}>
      <CardMedia
        style={{ textAlign: 'end', padding: 8}}
        sx={{ height: 165, opacity: props.item.isActive ? 1 : 0.4 }}
        image={menuItemImageSrc}
        children={
          <div>
            <input
                    type="file"
                    ref={fileMenuItemInputRef}
                    style={{ display: 'none' }}
                    onChange={handleImageChange}
                  />
                  { uploadStatus.inProgress ? <CircularProgress size={25} style={{ marginTop: 8, marginRight: 8}} /> : 
                    <Tooltip title={t('menuItems.changePicture')} placement="top-start">
                        <IconButton 
                          aria-label="settings"
                          onClick={handleImageButtonClick}>
                          <CameraAltIcon />
                        </IconButton>
                  </Tooltip>
                  }
                  
                  
          </div>
          }
      />
      <CardContent>
        <Typography gutterBottom variant="h6" component="div">
          {props.name}
        </Typography>
        <Typography variant="" color="text.secondary">
          {props.item.name} <span style={{ fontSize: 22 }}>${props.item.price}</span>
        </Typography>
      </CardContent>
      <CardActions disableSpacing style={{ justifyContent: 'space-between' }}>
        <div>
          <ExpandMore
              expand={expanded}
              onClick={handleExpandClick}
              aria-expanded={expanded}
              aria-label="show more"
            >
              <ExpandMoreIcon />
          </ExpandMore>
        </div>
        <div style={{ display: 'flex' }}>
          <MenuItemCreatorEditorButton menuItem={props.item} menu={props.menu} onMenuChange={handleEditMenuItem}/>
          <MenuItemRemoveIconButton item={props.item} onRemoveMenuItem={handleRemoveMenuItem}/>
        </div>
      </CardActions>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent style={{ opacity: props.item.isActive ? 1 : 0.4 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'gray' }}>
              <AccessAlarmIcon /><span>{t('menuItems.timeToPrepare')}</span> 
            </div>
            <span style={{ display: 'flex', marginTop: 8, marginBottom: 16 }}>
              {props.item.timeToPrepare} {t('menuItems.minutes')}
            </span>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'gray'}}>
                <FormatListBulletedIcon /><span>{t('menuItems.ingredients')}</span> 
            </div>

            <Typography paragraph style={{ display: 'flex', textAlign: 'justify', marginTop: 8, marginBottom: 16 }}>
              {props.item.ingredients}
            </Typography>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'gray'}}>
                <NotesIcon /><span>{t('menuItems.description')}</span> 
            </div>

            <Typography paragraph style={{ display: 'flex', textAlign: 'justify', marginTop: 8, marginBottom: 16 }}>
              {props.item.description}
            </Typography>
          </div>
        </CardContent>
      </Collapse>
    </Card>
    </div>
  );
}

MenuItemReadView.propTypes = {
  // id: PropTypes.number,
  // name: PropTypes.string,
}